import { getAppConfigValue } from "@elliemae/pui-app-sdk";
import { v4 as uuidv4 } from "uuid";
import { formatDate, isEmpty, partitionArray, safeSum } from "@frontend/utils";
const generateSectionTitle = (description, editable = false, handlerName) => ({
  id: uuidv4(),
  description,
  editable,
  type: "groupTitle",
  handlerName
});
const formatPriceValue = (p) => {
  if (p === null || p === void 0 || Number.isNaN(p)) return "";
  if (p === false) return "0.000";
  return Number(Number(p)?.toFixed(3));
};
const formatPriceValuePercent = (p) => {
  if (p === null || p === void 0 || Number.isNaN(p)) return "";
  if (p === false) return "0.000%";
  return `${Number(p)?.toFixed(3)}%`;
};
const parseAdjustment = (adjustment) => {
  return {
    ...adjustment,
    rate: formatPriceValuePercent(adjustment.rate),
    price: formatPriceValue(adjustment.price),
    currentRate: formatPriceValuePercent(adjustment.currentRate),
    currentPrice: formatPriceValue(adjustment.currentPrice),
    change: formatPriceValue(adjustment.change),
    type: "regular",
    id: adjustment.id || uuidv4()
  };
};
const formatAdjustmentsOrConcessions = ({
  adjustments,
  isValidate,
  rowsBeforeEdit,
  isConcession = false,
  pricingHasChanged
}) => adjustments.map((adj) => {
  const newAdj = {
    ...adj,
    id: adj.id || uuidv4(),
    type: "regular",
    currentPrice: formatPriceValue(adj.price),
    price: formatPriceValue(adj.price),
    currentRate: formatPriceValuePercent(adj.rate),
    rate: formatPriceValuePercent(adj.rate),
    approvalDate: formatDate(adj.approvalDate, "Calendar")
  };
  if (pricingHasChanged && isValidate && !isEmpty(rowsBeforeEdit) && newAdj.adjustmentType) {
    const oldPrice = rowsBeforeEdit.find(
      (oldAdj) => oldAdj.id === newAdj.id || oldAdj.adjustmentType === newAdj.adjustmentType && oldAdj.description === newAdj.description
    )?.price;
    newAdj.price = formatPriceValue(oldPrice) || "-";
    const oldRate = rowsBeforeEdit.find(
      (oldAdj) => oldAdj.id === newAdj.id || oldAdj.adjustmentType === newAdj.adjustmentType && oldAdj.description === newAdj.description
    )?.rate;
    newAdj.rate = formatPriceValuePercent(oldRate) || "-";
  }
  if (!pricingHasChanged) {
    newAdj.description = adj.description;
  }
  if (isConcession) {
    newAdj.description = formatDate(adj.approvalDate, "Calendar");
  }
  return newAdj;
});
const calculateVariationsTotal = (loanVariations, key = "price") => {
  return [].concat(...Object.values(loanVariations)).reduce(
    (sum, loanVariation) => sum + convertStrPercentToNumber(loanVariation[key]),
    0
  );
};
const convertStrPercentToNumber = (str) => {
  if (!str) return 0;
  if (typeof str === "number") return str;
  return Number(str.replace("%", ""));
};
const emptyAdj = {
  price: null,
  approvalDate: "-",
  description: "-"
};
const generatePricingGridRows = ({
  loanVariations = {},
  adjustments = [],
  isValidate = false,
  rowsBeforeEdit = [],
  pricingHasChanged,
  isBuyside = false,
  isSellSide = false,
  roles = [],
  rights = {}
}) => {
  let {
    LockExtensionAdjustment: lockExtensions,
    ReLockFeeAdjustment: reLockFees,
    CustomPriceAdjustment: customPriceAdjustments,
    CorporatePriceConcession,
    BranchPriceConcession
  } = loanVariations;
  lockExtensions = (lockExtensions || []).concat(
    (adjustments ?? []).filter(
      (adj) => adj.adjustmentType === "LockExtensionAdjustment"
    )
  );
  reLockFees = (reLockFees || []).concat(
    (adjustments ?? []).filter(
      (adj) => adj.adjustmentType === "ReLockFeeAdjustment"
    )
  );
  customPriceAdjustments = (customPriceAdjustments || []).concat(
    (adjustments ?? []).filter(
      (adj) => adj.adjustmentType === "CustomPriceAdjustment" && !isValidate
    )
  );
  CorporatePriceConcession = (CorporatePriceConcession || []).concat(
    (adjustments ?? []).filter(
      (adj) => adj.adjustmentType === "CorporatePriceConcession"
    )
  );
  BranchPriceConcession = (BranchPriceConcession || []).concat(
    (adjustments ?? []).filter(
      (adj) => adj.adjustmentType === "BranchPriceConcession"
    )
  );
  let oldLoanVariations;
  let oldExtraPrice;
  let oldExtraRate;
  if (isValidate && !isEmpty(rowsBeforeEdit)) {
    oldLoanVariations = {
      LockExtensionAdjustment: rowsBeforeEdit.find(
        (adj) => adj.adjustmentType === "LockExtensionAdjustment"
      ) || [],
      ReLockFeeAdjustment: rowsBeforeEdit.find(
        (adj) => adj.adjustmentType === "ReLockFeeAdjustment"
      ) || [],
      CustomPriceAdjustment: rowsBeforeEdit.find(
        (adj) => adj.adjustmentType === "CustomPriceAdjustment"
      ) || [],
      CorporatePriceConcession: rowsBeforeEdit.find(
        (adj) => adj.adjustmentType === "CorporatePriceConcession"
      ) || [],
      BranchPriceConcession: rowsBeforeEdit.find(
        (adj) => adj.adjustmentType === "BranchPriceConcession"
      ) || []
    };
    oldExtraPrice = calculateVariationsTotal(oldLoanVariations, "price");
    oldExtraRate = calculateVariationsTotal(oldLoanVariations, "rate");
  }
  const extraPrice = calculateVariationsTotal(
    {
      LockExtensionAdjustment: lockExtensions,
      ReLockFeeAdjustment: reLockFees,
      CustomPriceAdjustment: customPriceAdjustments,
      CorporatePriceConcession,
      BranchPriceConcession
    },
    "price"
  );
  const extraRate = calculateVariationsTotal(
    {
      LockExtensionAdjustment: lockExtensions,
      ReLockFeeAdjustment: reLockFees,
      CustomPriceAdjustment: customPriceAdjustments,
      CorporatePriceConcession,
      BranchPriceConcession
    },
    "rate"
  );
  let filteredAdjustments = adjustments.map((adj) => parseAdjustment(adj));
  let adjsProfitMarginRows = [];
  const viewHoldBackRole = 12;
  const viewLOCompRole = 20;
  const hasViewHoldBackrights = roles.some(
    ({ roleId }) => Number(roleId) === viewHoldBackRole
  );
  const hasLOCompViewRight = roles.some(
    ({ roleId }) => Number(roleId) === viewLOCompRole
  );
  [filteredAdjustments, adjsProfitMarginRows] = partitionArray(
    filteredAdjustments,
    ({ adjustmentType }) => adjustmentType !== "Holdback" && adjustmentType !== "Profit"
  );
  if (!hasViewHoldBackrights && !hasLOCompViewRight) {
    filteredAdjustments = filteredAdjustments.filter(
      (adjustment) => adjustment.description !== "L.O. Compensation"
    );
  }
  adjsProfitMarginRows = adjsProfitMarginRows.filter(({ margin }) => !margin);
  const getFinalPrice = filteredAdjustments.find(
    (el) => el.adjustmentType === "Final"
  );
  const currentPrice = getFinalPrice?.currentPrice && formatPriceValue(
    safeSum(Number(getFinalPrice.currentPrice), oldExtraPrice, extraPrice)
  );
  const price = getFinalPrice && formatPriceValue(
    safeSum(
      Number(getFinalPrice.price),
      isValidate && pricingHasChanged ? 0 : extraPrice
    )
  );
  const currentRate = getFinalPrice?.currentRate && formatPriceValue(
    safeSum(Number(getFinalPrice.currentRate), oldExtraRate, extraRate)
  );
  const rate = getFinalPrice && formatPriceValuePercent(
    safeSum(
      convertStrPercentToNumber(getFinalPrice.rate),
      isValidate && pricingHasChanged ? 0 : extraRate
    )
  );
  const finalPrice = getFinalPrice && {
    ...getFinalPrice,
    description: "FINAL PRICE",
    price,
    rate,
    ...getFinalPrice.currentPrice ? { currentPrice } : {},
    ...getFinalPrice.currentRate ? { currentRate } : {}
  };
  const getBasePrice = filteredAdjustments.find(
    (el) => el.adjustmentType === "Base"
  );
  const basePrice = getBasePrice && {
    ...getBasePrice,
    description: "Base Price"
  };
  const adjustmentTypesBlacklist = [
    "Base",
    "Final",
    "LockExtensionAdjustment",
    "ReLockFeeAdjustment",
    "CustomPriceAdjustment",
    "CorporatePriceConcession",
    "BranchPriceConcession"
  ];
  const adjustmentRows = filteredAdjustments.filter(
    (el) => !adjustmentTypesBlacklist.includes(el.adjustmentType) && !el.margin
  );
  const emptyAdjArr = isBuyside ? [] : [emptyAdj];
  if (getAppConfigValue("partner-ui.showPricingDetails")) {
    return [
      ...basePrice ? [basePrice] : [],
      generateSectionTitle("Adjustments"),
      ...adjustmentRows,
      ...[
        ...rights.viewProfitMargin && !isSellSide ? [generateSectionTitle("Profitability"), ...adjsProfitMarginRows] : [],
        generateSectionTitle("Lock Extensions"),
        ...formatAdjustmentsOrConcessions({
          adjustments: lockExtensions || emptyAdjArr,
          isValidate,
          rowsBeforeEdit,
          isConcession: false,
          pricingHasChanged
        }),
        generateSectionTitle(
          "Re-Lock Fees",
          rights.editRelockFee && rights.accessBuyside,
          "ReLockFeeAdjustment"
        ),
        ...formatAdjustmentsOrConcessions({
          adjustments: reLockFees || emptyAdjArr,
          isValidate,
          rowsBeforeEdit,
          isConcession: false,
          pricingHasChanged
        }),
        generateSectionTitle(
          "Custom Adjustments",
          rights.editCustomAdjustments && rights.accessBuyside,
          "CustomPriceAdjustment"
        ),
        ...formatAdjustmentsOrConcessions({
          adjustments: customPriceAdjustments || emptyAdjArr,
          isValidate,
          rowsBeforeEdit,
          isConcession: false,
          pricingHasChanged
        }),
        ...!isSellSide ? [
          generateSectionTitle(
            "Corporate Price Concessions",
            rights.editConcessions && rights.accessBuyside,
            "CorporatePriceConcession"
          ),
          ...formatAdjustmentsOrConcessions({
            adjustments: CorporatePriceConcession || emptyAdjArr,
            isValidate,
            rowsBeforeEdit,
            isConcession: true,
            pricingHasChanged
          }),
          generateSectionTitle(
            "Branch Price Concessions",
            rights.editConcessions && rights.accessBuyside,
            "BranchPriceConcession"
          ),
          ...formatAdjustmentsOrConcessions({
            adjustments: BranchPriceConcession || emptyAdjArr,
            isValidate,
            rowsBeforeEdit,
            isConcession: true,
            pricingHasChanged
          })
        ] : []
      ],
      ...finalPrice ? [finalPrice] : []
    ];
  }
  return [
    ...basePrice ? [basePrice] : [],
    generateSectionTitle("Adjustments"),
    ...adjustmentRows,
    ...[
      ...rights.viewProfitMargin ? [generateSectionTitle("Profitability"), ...adjsProfitMarginRows] : []
    ],
    ...finalPrice ? [finalPrice] : []
  ];
};
export {
  generatePricingGridRows
};
